import { makeStyles } from '@material-ui/styles'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TextAreaAutosize from 'components/common/TextareaAutosize'
import { useCreateNoteMutation } from 'store/modules/PatientNotes'
import { trackEvent } from 'utils/analyticsUtils'
import { mapToCreateNoteInput } from 'utils/mappers/noteMappers'
import useShowSnackbar from 'hooks/useShowSnackbar'
import { UserNoteVisiblities } from 'logic/patientNotesLogic'
import GrinButton from 'components/common/buttons/GrinButton/GrinButton'
import NoteVisibilityMenuItem from './NoteVisibilityMenuItem'
import useNoteVisibilityOptions from './useNoteVisibilityOptions'
import InputIcon from './icons/InputIcon'

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    marginTop: 10
  },
  inputContainer: {
    position: 'relative',
    flex: 1,
    height: 100,
    borderRadius: 10,
    border: '1px solid var(--text-color-4)',
    overflow: 'hidden',
    padding: '4px 20px',
    backgroundColor: ({ isLoading }) => (isLoading ? 'var(--bg-color-2)' : 'var(--bg-color-10)')
  },
  noteInput: {
    height: 100,
    border: 'none!important',
    fontSize: '14px !important'
  },
  addButtonContainer: {
    position: 'absolute',
    right: 5,
    bottom: 5
  },
  addButton: {
    width: 'auto !important',
    height: 32
  },
  addButtonIconContainer: {
    margin: 0
  }
}))

const NoteInput = () => {
  const { t } = useTranslation()

  const [noteContent, setNoteContent] = useState('')

  const [createNote, { isLoading, isError: isFailedCreatingNote }] = useCreateNoteMutation()
  useShowSnackbar({
    isOpen: isFailedCreatingNote,
    message: t('pages.patients.patientSwitchableView.notes.createNoteError')
  })

  const handleAddNote = useCallback(
    async (visibility = UserNoteVisiblities.Public) => {
      const payload = mapToCreateNoteInput({
        noteContent: noteContent.trim(),
        visibility
      })

      trackEvent('Patient notes - Note added', payload)
      await createNote({ variables: payload })

      setNoteContent('')
    },
    [createNote, noteContent]
  )

  const { visibilityOptions } = useNoteVisibilityOptions({ onVisibilitySelected: handleAddNote })

  const classes = useStyles({ isLoading })
  return (
    <div className={classes.container}>
      <div className={classes.inputContainer}>
        <TextAreaAutosize
          rowsMax={4}
          style={{ bright: true }}
          className={classes.noteInput}
          placeholder={t('pages.patients.patientSwitchableView.notes.addNotePlaceholder')}
          keepErrorContainerWhenInactive={false}
          value={noteContent}
          isDisabled={isLoading}
          setValue={setNoteContent}
          id="add-note-input"
        />
        <div className={classes.addButtonContainer}>
          <GrinButton
            icon={<InputIcon style={{ marginRight: 8 }} />}
            label={t('pages.patients.patientSwitchableView.notes.addNote')}
            onClick={() => handleAddNote(UserNoteVisiblities.Public)}
            isLoading={isLoading}
            disabled={!noteContent.trim()}
            id="add-note-button"
            className={classes.addButton}
            iconContainerClassName={classes.addButtonIconContainer}
            OptionMenuItemComponent={NoteVisibilityMenuItem}
            options={visibilityOptions}
          />
        </div>
      </div>
    </div>
  )
}

export default NoteInput
